import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Divider,
  Chip,
} from '@mui/material';
import { useTranslation } from 'src/hooks/useTranslationHook';
import { FC } from 'react';
import { useHealthAndSafetyState } from '../../../../contexts/healthAndSafety.context';

const HealthIncidentsDetailsView: FC = () => {
  const { translateText } = useTranslation();
  const { selectedIncident } = useHealthAndSafetyState();

  if (!selectedIncident) {
    return (
      <Box
        sx={{ width: '100%', height: '100%', overflow: 'auto', p: 3 }}
        data-testid="health-incidents-details-container"
      >
        <Typography>
          {translateText('administration.healthAndSafety.noData')}
        </Typography>
      </Box>
    );
  }

  const formatDate = (date: Date | string | undefined) => {
    if (!date) return translateText('common.nA');
    const d = typeof date === 'string' ? new Date(date) : date;
    return d.toLocaleDateString();
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'CRITICAL':
        return 'error';
      case 'HIGH':
        return 'warning';
      case 'MEDIUM':
        return 'info';
      case 'LOW':
        return 'success';
      default:
        return 'default';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'RESOLVED':
        return 'success';
      case 'CLOSED':
        return 'default';
      case 'IN_PROGRESS':
        return 'info';
      case 'OPEN':
        return 'warning';
      default:
        return 'default';
    }
  };

  return (
    <Box
      sx={{ width: '100%', height: '100%', overflow: 'auto', p: 3 }}
      data-testid="health-incidents-details-container"
    >
      <Typography variant="h5" fontWeight="bold" sx={{ mb: 3 }}>
        {translateText('administration.healthAndSafety.healthIncidentsDetails')}
      </Typography>

      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
            {translateText('common.description')}
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4} data-testid="incident-type">
              <Typography variant="caption" color="textSecondary">
                {translateText('administration.healthAndSafety.columns.type')}
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {selectedIncident.incidentType}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={4} data-testid="incident-severity">
              <Typography
                variant="caption"
                color="textSecondary"
                sx={{ display: 'block' }}
              >
                {translateText(
                  'administration.healthAndSafety.columns.severity'
                )}
              </Typography>
              <Chip
                label={selectedIncident.severity}
                color={getSeverityColor(selectedIncident.severity) as any}
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} data-testid="incident-status">
              <Typography
                variant="caption"
                color="textSecondary"
                sx={{ display: 'block' }}
              >
                {translateText('administration.healthAndSafety.columns.status')}
              </Typography>
              <Chip
                label={selectedIncident.status}
                color={getStatusColor(selectedIncident.status) as any}
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} data-testid="incident-location">
              <Typography variant="caption" color="textSecondary">
                {translateText(
                  'administration.healthAndSafety.columns.location'
                )}
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {selectedIncident.location}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={4} data-testid="incident-person-type">
              <Typography variant="caption" color="textSecondary">
                {translateText(
                  'administration.healthAndSafety.columns.personType'
                )}
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {selectedIncident.affectedPersonType}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={4} data-testid="incident-person-id">
              <Typography variant="caption" color="textSecondary">
                {translateText(
                  'administration.healthAndSafety.columns.personId'
                )}
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {selectedIncident.affectedPersonId}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={4} data-testid="incident-reported-by">
              <Typography variant="caption" color="textSecondary">
                {translateText(
                  'administration.healthAndSafety.columns.reportedBy'
                )}
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {selectedIncident.reportedBy}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={4} data-testid="incident-id">
              <Typography variant="caption" color="textSecondary">
                {translateText(
                  'administration.healthAndSafety.labels.incidentId'
                )}
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {selectedIncident.id}
              </Typography>
            </Grid>
            {selectedIncident.description && (
              <Grid item xs={12} data-testid="incident-description">
                <Typography variant="caption" color="textSecondary">
                  {translateText('common.description')}
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {selectedIncident.description}
                </Typography>
              </Grid>
            )}
            {selectedIncident.symptoms && (
              <Grid item xs={12} data-testid="incident-symptoms">
                <Typography variant="caption" color="textSecondary">
                  {translateText(
                    'administration.healthAndSafety.labels.symptoms'
                  )}
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {selectedIncident.symptoms}
                </Typography>
              </Grid>
            )}
          </Grid>
        </CardContent>
      </Card>

      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
            {translateText(
              'administration.healthAndSafety.labels.datesTimeline'
            )}
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4} data-testid="incident-date">
              <Typography variant="caption" color="textSecondary">
                {translateText('administration.healthAndSafety.columns.date')}
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {formatDate(selectedIncident.incidentDate)}
              </Typography>
            </Grid>
            <Grid
              item
              xs={12}
              sm={6}
              md={4}
              data-testid="incident-reported-date"
            >
              <Typography variant="caption" color="textSecondary">
                {translateText(
                  'administration.healthAndSafety.labels.reportedDate'
                )}
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {formatDate(selectedIncident.reportedDate)}
              </Typography>
            </Grid>
            {selectedIncident.followUpDate && (
              <Grid
                item
                xs={12}
                sm={6}
                md={4}
                data-testid="incident-followup-date"
              >
                <Typography variant="caption" color="textSecondary">
                  {translateText(
                    'administration.healthAndSafety.labels.followUpDate'
                  )}
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {formatDate(selectedIncident.followUpDate)}
                </Typography>
              </Grid>
            )}
          </Grid>
        </CardContent>
      </Card>

      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
            {translateText(
              'administration.healthAndSafety.labels.medicalInformation'
            )}
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4} data-testid="incident-first-aid">
              <Typography
                variant="caption"
                color="textSecondary"
                sx={{ display: 'block' }}
              >
                {translateText(
                  'administration.healthAndSafety.columns.firstAid'
                )}
              </Typography>
              <Chip
                label={
                  selectedIncident.firstAidProvided
                    ? translateText('common.yes')
                    : translateText('common.no')
                }
                color={
                  selectedIncident.firstAidProvided ? 'success' : 'default'
                }
                size="small"
              />
            </Grid>
            <Grid
              item
              xs={12}
              sm={6}
              md={4}
              data-testid="incident-medical-care"
            >
              <Typography
                variant="caption"
                color="textSecondary"
                sx={{ display: 'block' }}
              >
                {translateText(
                  'administration.healthAndSafety.columns.medicalCare'
                )}
              </Typography>
              <Chip
                label={
                  selectedIncident.medicalAttentionRequired
                    ? translateText('common.yes')
                    : translateText('common.no')
                }
                color={
                  selectedIncident.medicalAttentionRequired
                    ? 'warning'
                    : 'default'
                }
                size="small"
              />
            </Grid>
            {selectedIncident.hospitalVisited && (
              <Grid item xs={12} sm={6} md={4} data-testid="incident-hospital">
                <Typography variant="caption" color="textSecondary">
                  {translateText(
                    'administration.healthAndSafety.labels.hospitalVisited'
                  )}
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {selectedIncident.hospitalVisited}
                </Typography>
              </Grid>
            )}
            <Grid
              item
              xs={12}
              sm={6}
              md={4}
              data-testid="incident-followup-required"
            >
              <Typography
                variant="caption"
                color="textSecondary"
                sx={{ display: 'block' }}
              >
                {translateText(
                  'administration.healthAndSafety.labels.followUpRequired'
                )}
              </Typography>
              <Chip
                label={
                  selectedIncident.followUpRequired
                    ? translateText('common.yes')
                    : translateText('common.no')
                }
                color={selectedIncident.followUpRequired ? 'info' : 'default'}
                size="small"
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {(selectedIncident.resolution || selectedIncident.preventiveMeasures) && (
        <Card variant="outlined">
          <CardContent>
            <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
              {translateText(
                'administration.healthAndSafety.labels.resolutionPrevention'
              )}
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              {selectedIncident.resolution && (
                <Grid item xs={12} data-testid="incident-resolution">
                  <Typography variant="caption" color="textSecondary">
                    {translateText(
                      'administration.healthAndSafety.labels.resolution'
                    )}
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {selectedIncident.resolution}
                  </Typography>
                </Grid>
              )}
              {selectedIncident.preventiveMeasures && (
                <Grid item xs={12} data-testid="incident-preventive-measures">
                  <Typography variant="caption" color="textSecondary">
                    {translateText(
                      'administration.healthAndSafety.labels.preventiveMeasures'
                    )}
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {selectedIncident.preventiveMeasures}
                  </Typography>
                </Grid>
              )}
            </Grid>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default HealthIncidentsDetailsView;
